/**
 * CrmApi
 * @requires Axios
 * @version 1.0
 */

import axios from 'axios';
import Router from './../router'
import Store from "../store";

let Api = (function (undefined) {
    'use strict'; // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Strict_mode

    /**
     * @type {object}
     */
    let axiosInstance;

    let accessToken = null;

    let refreshToken = null;

    /**
     * @type {object}
     */
    let params = {};

    let isRefreshing = false;

    let refreshPromise;

    let baseUrl;

    let adminUserTypeId;

    let crmRest = function () {
        let self = this;

        self.init = function () {
            self.baseUrl = process.env.VUE_APP_API_URL;
            self.adminUserTypeId = 1;

            self.getAxiosInstance();
            return self.userGetAccount();
        };

        self.getAxiosInstance = function () {
            self.axiosInstance = axios.create({
                baseURL: self.baseUrl
            });

            self.axiosInstance.interceptors.response.use(response => {
                return response;
            }, error => {
                const { config: originalRequest, response } = error;

                if (!response) {
                    return Promise.reject(error);
                }

                // // This feature is to be implemented at a later time
                // if (response.status === 403 && data.error === 'user_tracking.not_accepted') {
                //     Router.push({ name: 'account-accept-activity-flow' });
                //     return;
                // }

                if (response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    if (!isRefreshing) {
                        isRefreshing = true;

                        refreshPromise = self
                            .refreshAccessToken(self.getLocalValue('refresh_token'))
                            .finally(() => {
                                isRefreshing = false;
                            });
                    }

                    return refreshPromise.then(newToken => {
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return self.axiosInstance(originalRequest);
                    });
                }

                return Promise.reject(error);
            });

            return self.axiosInstance;
        };

        /**
         * @param {string} key
         * @param {mixed} value.
         * @return {Api}
         */
        self.addParam = function (key, value) {
            self.params[key] = value;

            return self;
        };

        /**
         * @param {object} params
         * @return {Api}
         */
        self.addParams = function (params) {
            for (key in params) {
                self.addParam(key, params[key]);
            }

            return self;
        };

        /**
         * @param {string} key
         * @param {mixed} value
         * @returns {mixed}
         */
        self.getParam = function (key, value) {
            if (self.params[key]) {
                return self.params[key];
            }

            return value;
        };

        /**
         * @returns {object}
         */
        self.getParams = function () {
            return self.params;
        };

        /**
         * @returns {Api}
         */
        self.clearParams = function () {
            self.params = {};

            return self;
        };

        /**
         * @param {int} page
         * @param {int} limit
         * @returns {Api}
         */
        self.setPageParams = function (page, limit) {
            self.params.page = page;
            self.params.limit = limit || 999;

            return self;
        };

        /**
         * @param {string} sort
         * @param {string} dir
         * @returns {Api}
         */
        self.setSortParams = function (sort, dir) {
            self.params.sortby = sort;
            self.params.sortdir = dir || 'asc';

            return self;
        };

        /**
         * @param {object} headers
         * @returns {object}
         */
        self.getHeaders = function (config) {
            config = config || {};

            if (config.headers) {
                config.headers.Authorization = 'Bearer ' + self.getLocalValue('access_token');
            } else {
                config.headers = {
                    Authorization: 'Bearer ' + self.getLocalValue('access_token')
                };
            }

            return config;
        };

        /**
         * @param {string} path
         * @returns {Promise<T> | *}
         */
        self.get = function (path) {
            return self.axiosInstance.get(path,
                self.getHeaders({
                    params: self.getParams()
                }));
        };

        /**
         * @param {string} path
         * @returns {Promise<T> | *}
         */
        self.delete = function (path) {
            return self.axiosInstance.delete(path,
                self.getHeaders({
                    params: self.getParams()
                }));
        };

        /**
         * @param {string} path
         * @param {object} params
         * @param {object} headers
         * @returns {Promise<T> | *}
         */
        self.post = function (path, params, headers) {
            return self.axiosInstance.post(path, params || {}, self.getHeaders(headers));
        };

        /**
         * @param {string} path
         * @param {object} params
         * @param {object} headers
         * @returns {Promise<T> | *}
         */
        self.put = function (path, params, headers) {
            return self.axiosInstance.put(path, params || {}, self.getHeaders(headers));
        };

        /**
         * @param {object} params
         * @returns {object}
         */
        self.getFormData = function (params) {
            var data = new FormData();

            for (var key in params) {
                data.append(key, params[key]);
            }

            return data;
        },

            /**
             * Get the access token from the API using username and password
             */
            self.getAccessToken = function (username, password) {
                const requestBody = {
                    username: username,
                    password: password
                };
                const config = {
                    headers: {
                        'Authorization': 'Basic U1RFUkM6U1RFUkNPbXJpbg=='
                    }
                };
                return self.axiosInstance.post('access-token', requestBody, config)
                    .then(function (response) {
                        // Saving access token in cookie with same lifetime as access_token
                        self.setLocalValue(
                            'access_token',
                            response.data.access_token,
                            parseInt(response.data.expires_in) / 2
                        );
                        // Also store refresh token in cookie, lifetime = access_token * 24 * 28 (4 weeks)
                        self.setLocalValue(
                            'refresh_token',
                            response.data.refresh_token,
                            parseInt(response.data.expires_in) * 24 * 28
                        );
                    }).catch(function (error) {
                        return Promise.reject(error);
                    });
            };

        /**
         * Refresh the access token from the API using refresh token
         */
        self.refreshAccessToken = function (refresh_token) {
            if (!refresh_token) {
                return false;
            }

            const requestBody = {
                grant_type: 'refresh_token',
                refresh_token: refresh_token,
            };

            const requestConfig = {
                headers: {
                    'Authorization': 'Basic U1RFUkM6U1RFUkNPbXJpbg==',
                }
            };

            return self.axiosInstance
                .post('access-token', requestBody, requestConfig)
                .then(response => {
                    console.log('Successfully refreshed access token')
                    const data = response.data

                    // Saving access token in cookie with same lifetime as access_token
                    self.setLocalValue('access_token', data.access_token, parseInt(data.expires_in));

                    // Also store refresh token in cookie, lifetime = access_token * 24
                    self.setLocalValue('refresh_token', data.refresh_token, parseInt(data.expires_in) * 24);

                    return data.access_token
                }).catch(error => {
                    console.warn("Logging out user because of failed refresh token request.", error);
                    self.userLogout();

                    return Promise.reject(error);
                });
        };

        /**
         * Store a key/value pair in cookie
         * @param key
         * @param token
         * @param expire_seconds
         */
        self.setLocalValue = function (key, token, expire_seconds) {
            self.setCookie(key, token, expire_seconds);
        };

        self.getLocalValue = function (key) {
            return self.getCookie(key);
        };

        self.removeLocalValue = function (key) {
            self.setCookie(key, "", 0);
        };

        self.getCookie = function (cookie_name) {
            let name = cookie_name + "=";
            let ca = document.cookie.split(';');

            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];

                while (c.charAt(0) === ' ') {
                    c = c.substring(1);
                }

                if (c.indexOf(name) === 0) {
                    return c.substring(name.length, c.length);
                }
            }
        };

        /**
         * @param {string} cookie_name
         * @param {string} value
         * @param {int} expire_seconds
         */
        self.setCookie = function (cookie_name, value, expire_seconds) {
            let d = new Date();
            d.setTime(d.getTime() + (expire_seconds * 1000));
            let expires = "expires=" + d.toUTCString();
            let secureFlag = window.location.protocol === 'https:' ? ';Secure' : '';
            document.cookie = cookie_name + "=" + value + ";" + expires + ";path=/;SameSite=Lax" + secureFlag;
        };

        self.userLogin = function (params) {
            if (params['username'] && params['password']) {
                return self.getAccessToken(params['username'], params['password'])
            }
        };

        /**
         * Logout the user, remove data from cookies and vue-store
         * Redirect to login page
         */
        self.userLogout = function () {
            self.removeLocalValue('refresh_token');
            self.removeLocalValue('access_token');
            self.removeLocalValue('user');
            self.removeLocalValue('user_permissions');
            Store.commit('RESET_USER');
            Router.push({ name: 'login' });
        };

        self.userGetAccount = function () {
            if (Store && Store.getters.getUser && Store.getters.getUser.id) {
                return Promise.resolve({
                    'data': {
                        'data': Store.getters.getUser,
                        'status': 200
                    }
                });
            }

            if (!self.getLocalValue('access_token') && !self.getLocalValue('refresh_token')) {
                return;
            }

            return this.userGetAccountFromApi()
        };

        self.userGetAccountFromApi = function () {
            Store.commit('SET_LOADING', true);

            return self.axiosInstance.get('accounts/me', this.getHeaders())
                .then(function (response) {
                    let user = response.data.data;
                    let permissions = response.data.meta.permissions;
                    Store.commit('SET_USER', user);
                    Store.commit('SET_USER_PERMISSIONS', permissions);
                    Store.commit('SET_LOADING', false);
                    self.setLocalValue('user', JSON.stringify(user), 3600);
                    self.setLocalValue('user_permissions', JSON.stringify(permissions), 3600);
                    return response;
                })
                .catch(function (error) {
                    Store.commit('SET_LOADING', false);
                    return Promise.reject(error);
                });
        };

        self.userUpdateProfile = function (params) {
            return self.axiosInstance.post('accounts/' + params.id + '/update', params, this.getHeaders())
                .then(function (response) {
                    Store.commit('SET_USER', response.data.data);

                    return response
                }).catch(function (error) {
                    return Promise.reject(error);
                });
        };

        self.userChangePassword = function (params) {
            return self.axiosInstance.post('accounts/' + params.id + '/update', params, this.getHeaders())
                .then(function (response) {
                    return response;
                }).catch(function (error) {
                    return Promise.reject(error);
                });
        };

        self.userHasAccess = function (permission, to) {
            let access = true;
            if (permission !== 'public' && !Store.getters.getUser.loggedin) {
                return false;
            }
            if (permission === 'public') {
                return true;
            }
            if (!Store.getters.userHasPermission(permission)) {
                access = false;
            }

            // Temporary KI MVP gate — remove with the feature
            if (to && to.meta && to.meta.kiMvpAllowlist && !Store.getters.userCanAccessKiMvp) {
                access = false;
            }

            // Check for user_type_id
            // If not admin, limit access to specific customer_id
            // If no customer attached to account, then user has no access
            let userTypeId = parseInt(Store.getters.getUser.user_type_id);
            if (userTypeId !== self.adminUserTypeId) {
                let userCustomerId = Store.getters.getUser.customer_id;
                if (!userCustomerId) {
                    access = false;
                }
                if (to.params.customer && (parseInt(to.params.customer) !== parseInt(userCustomerId))) {
                    access = false;
                }
            }

            return access;
        };
    };

    return crmRest;
})();

export default new Api();
